import { ServiceRegistry } from '../../core/ServiceRegistry';
import type { EcsRegistry } from '../../ecs';
import type { CombatTarget } from './combatTargets';
import { reachToBody } from '../../../shared/logic/BodyReach';
import type { IHitPresentationTarget } from '../../../shared/combat/hitPresentation';

/**
 * ECS-backed combat target source (authoritative-ECS arc K3, 2026-06-15).
 *
 * Replaces the legacy `entities/EntityRegistry` + the MobEntity/NPCEntity/
 * PlayerEntity mirror wrappers as combat's one entity source. Hostile targets
 * are read straight from the ECS ComponentBags (transform position + the
 * kind/hostile discriminator written at spawn in K1); HP is resolved live from
 * the owning manager (until K4 makes bag.stats authoritative), and the player
 * comes from the live PlayerManager. Drop-in for CombatManager — same method
 * names as the retired registry.
 */
/** A live mob/NPC's hitbox size, when it has one. */
function bodySize(owner: { width?: unknown; height?: unknown } | null): { width?: number; height?: number } {
  const width = typeof owner?.width === 'number' && owner.width > 0 ? owner.width : undefined;
  const height = typeof owner?.height === 'number' && owner.height > 0 ? owner.height : undefined;
  return width && height ? { width, height } : {};
}

export class EcsCombatTargets {
  public readonly id = 'EcsCombatTargets';

  private ecs(): EcsRegistry | undefined {
    return ServiceRegistry.getInstance().get('EcsRegistry', { optional: true });
  }

  /** Live self-player (PlayerManager owns it). */
  public getPlayer(): any | null {
    const es = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    return es?.getPlayerManager?.()?.getPlayer?.() ?? null;
  }

  /** Live mob (sole HP owner until K4) for a given id, if any. */
  private getMob(id: string): any | null {
    const ms = ServiceRegistry.getInstance().get('MobSystem', { optional: true });
    return ms?.getManager?.()?.getMob?.(id) ?? null;
  }

  /** Live NPC data record for a given id, if any. */
  private getNpc(id: string): any | null {
    const es = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    return es?.getNPCManager?.()?.getNPCs?.()?.get?.(id) ?? null;
  }

  /** Build a CombatTarget from an ECS bag, resolving the live owner once (for
   *  HP + to disambiguate kind on bags without the render discriminator, e.g.
   *  player bags). Returns null only for bags with no transform. */
  private toTarget(id: string): CombatTarget | null {
    const bag = this.ecs()?.get(id);
    if (!bag?.transform) return null;
    const mob = this.getMob(id);
    const npc = mob ? null : this.getNpc(id);
    const kind: CombatTarget['type'] = bag.render?.kind ?? (mob ? 'mob' : npc ? 'npc' : 'player');
    const liveHp = mob?.health ?? npc?.health ?? bag.stats?.hp ?? 0;
    return {
      id,
      type: kind,
      name: bag.render?.spriteKey ?? id,
      x: bag.transform.x,
      y: bag.transform.y,
      health: liveHp,
      maxHealth: bag.stats?.maxHp ?? liveHp,
      level: bag.stats?.level,
      isHostile: bag.render?.hostile ?? false,
      ...bodySize(mob ?? npc),
    };
  }

  public getTarget(id: string): CombatTarget | null {
    return this.toTarget(id);
  }

  public isValidTarget(id: string): boolean {
    return this.toTarget(id) !== null;
  }

  /** Hostile mobs + hostile NPCs within range, nearest first (matches the
   *  retired registry: players are never returned here). */
  public getHostileTargetsInRange(centerX: number, centerY: number, range: number): CombatTarget[] {
    const ecs = this.ecs();
    if (!ecs) return [];
    const out: (CombatTarget & { _d: number })[] = [];
    for (const auth of ['server', 'replicated'] as const) {
      for (const id of ecs.withAuthority(auth)) {
        const bag = ecs.get(id);
        if (!bag?.render?.hostile || !bag.transform) continue;
        const t = this.toTarget(id);
        if (!t) continue;
        // To the BODY, not the centre (plan 07/08 reach audit): a flush
        // pig_mom is as near as a flush slime. Nearest body first.
        const d = reachToBody({ x: centerX, y: centerY }, t);
        if (d > range) continue;
        out.push({ ...t, _d: d });
      }
    }
    return out.sort((a, b) => a._d - b._d).map(({ _d, ...t }) => t);
  }

  public findNearestHostile(centerX: number, centerY: number, range = Infinity): CombatTarget | null {
    return this.getHostileTargetsInRange(centerX, centerY, range)[0] ?? null;
  }

  /** Server HP reconcile — write the live owner so the HP bar updates. */
  public setTargetHealth(id: string, health: number): void {
    const mob = this.getMob(id);
    if (mob) { mob.health = health; }
    const npc = this.getNpc(id);
    if (npc) { npc.health = health; }
    const bag = this.ecs()?.get(id);
    if (bag?.stats) bag.stats.hp = health;
  }

  /** Renderer-owned reaction target. Unlike getTargetSprite(), this is never
   * a coordinate-only ECS projection: consumers can only receive a live
   * presentation capability with renderer-correct tint/motion restoration. */
  public getTargetPresentation(id: string): IHitPresentationTarget | null {
    const mobTarget = this.getMob(id)?.getHitPresentationTarget?.();
    if (mobTarget) return mobTarget;

    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const npcTarget = entitySystem?.getNPCManager?.()?.getHitPresentationTarget?.(id);
    if (npcTarget) return npcTarget;

    return entitySystem?.getPlayerRenderer?.()?.getHitPresentationTarget?.(id) ?? null;
  }

  /** Target position for VFX anchoring (FCT, acquire-snap). Returns {x,y} —
   *  the only thing the old getTargetSprite consumers read. */
  public getTargetSprite(id: string): { x: number; y: number } | null {
    const t = this.ecs()?.get(id)?.transform;
    return t ? { x: t.x, y: t.y } : null;
  }
}
